import { Injectable, Injector } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormArray,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { UserAddress } from '@app/forms/subscription/definition/interface/subscription';
import * as _ from 'lodash';
import FormGroupArray from '../../services/FormGroupArray';
@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  protected countries: any[] = [];
  constructor(
    protected formConstructor: PrFormConstructor,
    protected injector: Injector,
  ) {
    super(formConstructor);
    injector
      .get(ReferenceDao)
      .getCountries()
      .subscribe(countries => {
        this.countries = countries;
      });
  }

  generateForm(entity: any) {
    let options: AbstractControlOptions = {};
    const { fiscalAddressIsNotContactAddress = false } = entity || {};

    return this.formBuilder.group(
      {
        ownerId: [entity.ownerId],
        userLastName: [entity?.userLastName || null],
        userFirstName: [entity?.userFirstName ? entity.userFirstName : null],
        fiscalAddressIsNotContactAddress: [
          !_.isNil(fiscalAddressIsNotContactAddress)
            ? fiscalAddressIsNotContactAddress
            : false,
        ],
        address: this.formBuilder.group({
          street: [
            entity && entity.addressStreet ? entity.addressStreet : null,
            Validators.required,
          ],
          zipCode: [
            entity && entity.addressZipCode ? entity.addressZipCode : null,
            // Validators.required,
          ],
          city: [
            entity && entity.addressCity
              ? entity.addressCity.toUpperCase()
              : null,
            Validators.required,
          ],
          country: [
            entity && entity.addressCountry
              ? {
                  id: this.findCountryId(entity.addressCountry),
                  value: this.findCountryValue(entity.addressCountry),
                  valueGb: this.findCountryValueGb(entity.addressCountry),
                }
              : null,
            Validators.required,
          ],
        }),
        fiscalAddress: this.formBuilder.group({
          street: [
            entity && entity.fiscalAddressStreet
              ? entity.fiscalAddressStreet
              : null,
            !!fiscalAddressIsNotContactAddress
              ? Validators.required
              : Validators.nullValidator,
          ],

          zipCode: [
            entity && entity.fiscalAddressZipCode
              ? entity.fiscalAddressZipCode
              : null,
          ],
          city: [
            entity && entity.fiscalAddressCity
              ? entity.fiscalAddressCity.toUpperCase()
              : null,
            !!fiscalAddressIsNotContactAddress
              ? Validators.required
              : Validators.nullValidator,
          ],
          country: [
            entity && entity.fiscalAddressCountry && entity.fiscalAddressCity
              ? this.countries.find(
                  ({ id }) => id === entity.fiscalAddressCountry,
                )?.value || entity.fiscalAddressCountry
              : null,
            !!fiscalAddressIsNotContactAddress
              ? Validators.required
              : Validators.nullValidator,
          ],
        }),
      },
      options,
    );
  }

  findCountryId(country: string): string {
    if (!country) {
      return country;
    }
    return (
      this.countries.find(c => c.id === country || c.value === country)?.id ||
      country
    );
  }

  findCountryValue(country: string): string {
    if (!country) {
      return country;
    }
    return (
      this.countries.find(c => c.id === country || c.value === country)
        ?.value || country
    );
  }

  findCountryValueGb(country: string): string {
    if (!country) {
      return country;
    }
    return (
      this.countries.find(c => c.id === country || c.value === country)
        ?.valueGb || country
    );
  }

  generate(entity: UserAddress[], option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
    });
  }
  extract() {
    const formValue = _.cloneDeep(this.form.value);
    const owners = formValue.owners.map(owner => {
      const address = this.transformAddress(owner.address);
      const fiscalAddress = this.transformFiscalAddress(owner.fiscalAddress);
      return {
        ..._.omit(owner, ['address', 'fiscalAddress']),
        ...address,
        ...fiscalAddress,
      };
    });
    const obj = {
      ...formValue,
      owners,
    };
    return obj.owners;
  }
  get owners() {
    return this.form.get('owners') as UntypedFormArray;
  }
  transformAddress(originalAddress: {
    street: string;
    street2?: string;
    zipCode: string;
    city: string;
    country: { id: string; valueGb: string; value: string };
  }) {
    const address = originalAddress
      ? (({ street, street2, city, zipCode, country, ...address }) => ({
          ...address,
          addressStreet: street || '',
          addressStreet2: street2 || '',
          addressCity: city || '',
          addressZipCode: zipCode || '',
          addressCountry: country?.id || country || '',
        }))(originalAddress)
      : {};
    return address;
  }

  transformAddressBack(originalAddress: {
    addressStreet: string;
    addressStreet2?: string;
    addressCity: string;
    addressZipCode: string;
    addressCountry: string;
  }) {
    const address = originalAddress
      ? (({
          addressStreet,
          addressStreet2,
          addressCity,
          addressZipCode,
          addressCountry,
          ...address
        }) => ({
          ...address,
          street: addressStreet || '',
          street2: addressStreet2 || '',
          city: addressCity || '',
          zipCode: addressZipCode || '',
          country: addressCountry || '',
        }))(originalAddress)
      : {};
    return address;
  }

  transformFiscalAddress(originalAddress: {
    street: string;
    street2?: string;
    zipCode: string;
    city: string;
    country: { id: string; valueGb: string; value: string };
  }) {
    const address = originalAddress
      ? (({ street, street2, city, zipCode, country, ...address }) => ({
          ...address,
          fiscalAddressStreet: street || '',
          fiscalAddressStreet2: street2 || '',
          fiscalAddressCity: city || '',
          fiscalAddressZipCode: zipCode || '',
          fiscalAddressCountry: country?.id || '',
        }))(originalAddress)
      : {};

    return address;
  }
}
